import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Activity, 
  Zap, 
  Database, 
  Wifi, 
  Clock, 
  AlertTriangle,
  TrendingUp,
  BarChart3,
  Settings
} from 'lucide-react'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { usePerformanceMonitor } from '../../utils/performance'
import { bundleOptimizer } from '../../utils/bundleOptimizer'
import { useThemeStore } from '../../stores/theme'
import { cn } from '../../utils/cn'

interface PerformanceMonitorProps {
  show?: boolean
  onClose?: () => void
  className?: string
}

export function PerformanceMonitor({ 
  show = false, 
  onClose,
  className 
}: PerformanceMonitorProps) {
  const themeType = useThemeStore((s) => s.themeType)
  const isChildTheme = themeType === 'child'
  
  const { getMetrics, getEntries, getSlowEntries, reportToAnalytics } = usePerformanceMonitor()
  const [metrics, setMetrics] = useState(getMetrics())
  const [, setEntries] = useState(getEntries())
  const [slowEntries, setSlowEntries] = useState(getSlowEntries())
  const [bundleAnalysis, setBundleAnalysis] = useState(bundleOptimizer.analyzeBundle())
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics(getMetrics())
      setEntries(getEntries())
      setSlowEntries(getSlowEntries())
      setBundleAnalysis(bundleOptimizer.analyzeBundle())
    }, 1000)

    return () => clearInterval(interval)
  }, [getMetrics, getEntries, getSlowEntries])

  const getPerformanceScore = () => {
    let score = 100
    
    if (metrics.loadTime > 3000) score -= 20
    if (metrics.renderTime > 1000) score -= 15
    if (metrics.memoryUsage > 50000000) score -= 10
    if (metrics.networkRequests > 50) score -= 10
    if (metrics.cacheHitRate < 80) score -= 15
    if (slowEntries.length > 0) score -= 20
    
    return Math.max(0, score)
  }

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600'
    if (score >= 70) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getScoreBadge = (score: number) => {
    if (score >= 90) return { variant: 'secondary' as const, text: 'Excellent' }
    if (score >= 70) return { variant: 'outline' as const, text: 'Good' }
    return { variant: 'destructive' as const, text: 'Needs Improvement' }
  }

  const performanceScore = getPerformanceScore()
  const scoreBadge = getScoreBadge(performanceScore)

  if (!show) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className={cn(
        "fixed bottom-4 inset-e-4 z-50 w-96 max-h-96 overflow-hidden",
        className
      )}
    >
      <Card className={cn(
        "p-4 shadow-lg border",
        isChildTheme 
          ? "bg-linear-to-br from-blue-50 to-purple-50 border-child-primary/20" 
          : "bg-white border-therapist-primary/20"
      )}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Activity className={cn(
              "h-5 w-5",
              isChildTheme ? "text-child-primary" : "text-therapist-primary"
            )} />
            <h3 className={cn(
              "font-semibold",
              isChildTheme ? "text-child-primary font-child" : "text-therapist-primary font-therapist"
            )}>
              Performance Monitor
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            <Badge variant={scoreBadge.variant}>
              {scoreBadge.text}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <BarChart3 className="h-4 w-4" />
            </Button>
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
              >
                ×
              </Button>
            )}
          </div>
        </div>

        {/* Performance Score */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Performance Score</span>
            <span className={cn(
              "text-2xl font-bold",
              getScoreColor(performanceScore)
            )}>
              {performanceScore}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className={cn(
                "h-2 rounded-full",
                performanceScore >= 90 ? "bg-green-500" :
                performanceScore >= 70 ? "bg-yellow-500" : "bg-red-500"
              )}
              initial={{ width: 0 }}
              animate={{ width: `${performanceScore}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4 text-blue-500" />
            <div>
              <div className="text-xs text-gray-500">Load Time</div>
              <div className="text-sm font-medium">
                {metrics.loadTime.toFixed(0)}ms
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Zap className="h-4 w-4 text-yellow-500" />
            <div>
              <div className="text-xs text-gray-500">Render Time</div>
              <div className="text-sm font-medium">
                {metrics.renderTime.toFixed(0)}ms
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Database className="h-4 w-4 text-green-500" />
            <div>
              <div className="text-xs text-gray-500">Memory</div>
              <div className="text-sm font-medium">
                {(metrics.memoryUsage / 1024 / 1024).toFixed(1)}MB
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Wifi className="h-4 w-4 text-purple-500" />
            <div>
              <div className="text-xs text-gray-500">Requests</div>
              <div className="text-sm font-medium">
                {metrics.networkRequests}
              </div>
            </div>
          </div>
        </div>

        {/* Expanded Details */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3"
            >
              {/* Bundle Analysis */}
              <div>
                <h4 className="text-sm font-medium mb-2">Bundle Analysis</h4>
                <div className="text-xs space-y-1">
                  <div>Total Size: {(bundleAnalysis.totalSize / 1024).toFixed(1)} KB</div>
                  <div>Chunks: {Object.keys(bundleAnalysis.chunkSizes).length}</div>
                  {bundleAnalysis.largeModules.length > 0 && (
                    <div className="text-red-600">
                      Large Modules: {bundleAnalysis.largeModules.length}
                    </div>
                  )}
                </div>
              </div>

              {/* Slow Operations */}
              {slowEntries.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center">
                    <AlertTriangle className="h-4 w-4 text-red-500 me-1" />
                    Slow Operations
                  </h4>
                  <div className="text-xs space-y-1 max-h-20 overflow-y-auto">
                    {slowEntries.slice(0, 5).map((entry, index) => (
                      <div key={index} className="text-red-600">
                        {entry.name}: {entry.duration?.toFixed(0)}ms
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {bundleAnalysis.recommendations.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Recommendations</h4>
                  <div className="text-xs space-y-1">
                    {bundleAnalysis.recommendations.slice(0, 3).map((rec, index) => (
                      <div key={index} className="text-yellow-600">
                        • {rec}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Actions */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => reportToAnalytics()}
            className="flex items-center space-x-1"
          >
            <TrendingUp className="h-3 w-3" />
            <span>Report</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => bundleOptimizer.optimizeImports()}
            className="flex items-center space-x-1"
          >
            <Settings className="h-3 w-3" />
            <span>Optimize</span>
          </Button>
        </div>
      </Card>
    </motion.div>
  )
}

// Performance monitoring hook
export function usePerformanceMonitoring() {
  const [isMonitoring, setIsMonitoring] = useState(false)
  const { getMetrics, reportToAnalytics } = usePerformanceMonitor()

  const startMonitoring = () => {
    setIsMonitoring(true)
    // Start performance monitoring
  }

  const stopMonitoring = () => {
    setIsMonitoring(false)
    // Stop performance monitoring
  }

  const getPerformanceReport = () => {
    const metrics = getMetrics()
    return {
      ...metrics,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href
    }
  }

  const sendReport = () => {
    reportToAnalytics()
  }

  return {
    isMonitoring,
    startMonitoring,
    stopMonitoring,
    getPerformanceReport,
    sendReport
  }
}

// Performance widget for development
export function PerformanceWidget() {
  const [show, setShow] = useState(false)
  const { startMonitoring, stopMonitoring } = usePerformanceMonitoring()

  useEffect(() => {
    // Check multiple conditions for enabling performance monitoring
    const envEnabled = import.meta.env.VITE_ENABLE_PERFORMANCE_MONITOR !== 'false'
    const localStorageEnabled = localStorage.getItem('performance-monitoring-enabled') === 'true'
    const widgetVisible = localStorage.getItem('performance-widget-visible') === 'true'
    const isDev = import.meta.env.DEV
    
    // Enable if: dev mode AND (env enabled OR localStorage enabled) AND widget should be visible
    const shouldEnable = isDev && (envEnabled || localStorageEnabled) && widgetVisible
    
    if (shouldEnable) {
      setShow(true)
      startMonitoring()
    }

    return () => {
      if (shouldEnable) {
        stopMonitoring()
      }
    }
  }, [startMonitoring, stopMonitoring])

  // Don't show if disabled via environment variable, localStorage, or not in dev mode
  const envDisabled = import.meta.env.VITE_ENABLE_PERFORMANCE_MONITOR === 'false'
  const localStorageDisabled = localStorage.getItem('performance-monitoring-enabled') === 'false'
  const widgetHidden = localStorage.getItem('performance-widget-visible') === 'false'
  
  if (!import.meta.env.DEV || envDisabled || localStorageDisabled || widgetHidden) {
    return null
  }

  return (
    <PerformanceMonitor
      show={show}
      onClose={() => {
        setShow(false)
        localStorage.setItem('performance-widget-visible', 'false')
      }}
    />
  )
}
